import { isAfter, isBefore } from 'date-fns';
import { z } from 'zod';
import { DateFromIsoString } from '~/common/utils';
import { countrySchema, customerTypeSchema } from '~/root/domain';

const phoneSchema = z
  .string()
  .nullable()
  .transform((value) => (value ? value.replace(/[^\d]/, '') : value));
const dialCodeSchema = z.coerce.number().nullable();

export const marketingCampaignsSchema = z.object({
  tenHoursTurnaround: z.boolean().optional(),
});

export type MarketingCampaigns = z.infer<typeof marketingCampaignsSchema>;

export const customerSettingsSchema = z.object({
  settings: z.object({
    sharing_order_seen: z.boolean().nullish(),
  }),
});

export type CustomerSettings = z.infer<typeof customerSettingsSchema>['settings'];

export const Suspension = z
  .object({
    reason: z.string(),
    suspendsAt: DateFromIsoString,
  })
  .transform(({ suspendsAt, ...suspension }) => ({
    ...suspension,
    suspendsAt,
    isSuspended: isBefore(suspendsAt, new Date()),
    isOnGracePeriod: isAfter(suspendsAt, new Date()),
  }))
  .nullable();

export const customerSchema = z
  .object({
    address: z.string().nullable(),
    balance: z.number().nullable(),
    hidePrices: z.boolean().optional(),
    cardDigits: z.string().nullable(),
    city: z.string().nullable(),
    company: z.string().nullable(),
    country: countrySchema.nullable(),
    currency: z.string().nullable(),
    currencySymbol: z.string().nullable(),
    customerAccountId: z.number(), // null when user is staff
    /** 'owner' | 'admin' | 'manager' */
    customerRole: z.string(),
    customerType: customerTypeSchema, // null when user is staff
    dashboardUrl: z.string().nullable(), // TODO is it nullable?
    dialCode: dialCodeSchema,
    email: z.string(),
    hasCard: z.boolean(),
    hasCredits: z.boolean(),
    hasDailyCapacity: z.boolean(),
    id: z.number(),
    isStaff: z.boolean(),
    /** 'isRetainer' => (bool) $billingAccount?->hasActiveSubscription([SubscriptionProductType::RETAINER]) */
    isRetainer: z.boolean(),
    name: z.string(),
    /** 'needsQuestions' => optional($user->customerAccount)->isRegular() */
    needsQuestions: z.boolean(), // null when user is staff
    notifications: z
      .object({
        phone: phoneSchema,
        dialCode: dialCodeSchema,
      })
      .nullable(),
    orderId: z.number().nullable(), // null when user doesn't have any submitted orders
    phone: phoneSchema,
    /** 'purchasedPackages' => ($billingAccount ? $billingAccount->creditPackages()->exists() : false) */
    purchasedPackages: z.boolean(),
    state: z.string().nullable(),

    subscription: z.boolean(),
    subscriptionType: z.string().nullable(),
    /**
     * Includes names of products in customer's subscription
     * Initially added to disable insufficient funds in order form for users with "Pro" retainer plan
     * Can be removed if CA BE gonna be normalized
     */
    subscriptionProducts: z.array(z.string()).nullable(),
    supportsCustomFields: z.boolean(),
    supportsConfidentialOrder: z.boolean(),
    supportsRequiredPONumber: z.boolean().optional(),
    organisationName: z.string().nullable(),

    customerSuspension: Suspension,
    subscriptionSuspension: Suspension,
    // TODO BE doesn't send token in the user field for oauth login response
    // we're not relying on this token too much, so let's make it optional
    token: z.string().optional(),
    zip: z.string().nullable(),
    marketingCampaigns: marketingCampaignsSchema.optional(),
  })
  .merge(customerSettingsSchema);

export type CustomerUser = z.infer<typeof customerSchema>;

export const staffToCustomerSchemaDiff = {
  customerType: z.null(),
  customerRole: z.string().nullable(),
  needsQuestions: z.boolean().nullable(),
  orderId: z.null(),
  customerAccountId: z.null(),
  isStaff: z.literal(true),
};

export const staffSchema = customerSchema.extend(staffToCustomerSchemaDiff);

export type StaffUser = z.infer<typeof staffSchema>;

export const userSchema = customerSchema
  .or(staffSchema)
  .transform(({ customerSuspension, subscriptionSuspension, ...user }) => ({
    ...user,
    customerSuspension: customerSuspension
      ? {
          ...customerSuspension,
          isSuspended: isBefore(customerSuspension.suspendsAt, new Date()),
          isOnGracePeriod: isAfter(customerSuspension.suspendsAt, new Date()),
        }
      : null,
    subscriptionSuspension: subscriptionSuspension
      ? {
          ...subscriptionSuspension,
          isSuspended: isBefore(subscriptionSuspension.suspendsAt, new Date()),
          isOnGracePeriod: isAfter(subscriptionSuspension.suspendsAt, new Date()),
        }
      : null,
  }));

export type CustomerOrStaff = z.infer<typeof userSchema>;

export type User = z.infer<typeof userSchema>;
